/**
 * Authentication Event System
 * 
 * This module provides a global event system for authentication state changes.
 * It allows different parts of the application to react to authentication events
 * without tight coupling to the auth context.
 */

export enum AuthEvent {
  SESSION_EXPIRED = 'auth:session_expired',
  SESSION_INVALID = 'auth:session_invalid',
  LOGOUT_REQUIRED = 'auth:logout_required',
  AUTH_ERROR = 'auth:error'
}

interface AuthEventData {
  reason?: string;
  error?: unknown;
}

class AuthEventEmitter extends EventTarget {
  emit(event: AuthEvent, data?: AuthEventData) {
    this.dispatchEvent(new CustomEvent(event, { detail: data }));
  }

  on(event: AuthEvent, handler: (data: AuthEventData) => void) {
    const wrappedHandler = (e: Event) => {
      const customEvent = e as CustomEvent<AuthEventData>;
      handler(customEvent.detail || {});
    };
    this.addEventListener(event, wrappedHandler);
    return () => this.removeEventListener(event, wrappedHandler);
  }
}

export const authEvents = new AuthEventEmitter();